import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { X, LogIn, Lock, Trash2, Skull, AlertTriangle } from "lucide-react";
import WornVaultSection from "@/components/admin/WornVaultSection";
import MediaCleanupDialog from "@/components/admin/MediaCleanupDialog";
import MediaReaperPanel from "@/components/admin/MediaReaperPanel";

const BASE_AREAS = [
  { key: "MARKETPLACE", label: "Marketplace" },
  { key: "STILLTOUCHEDX", label: "stilltouchedX" },
  { key: "SOCIAL", label: "Social" },
];
// iter174 · Worn-Vault ist streng Admin-only (owner/admin). Moderator/Support
// sehen den Tab nicht (Compliance-/Audit-Daten).
const WORN_VAULT_AREA = { key: "WORN_VAULT", label: "Worn-Vault", icon: Lock };

const PAGE_SIZE = 24;

function usageHref(u) {
  if (u.kind === "listing" || u.kind === "listing_extra" || u.kind === "listing_media") {
    return u.area_scope === "STILLTOUCHEDX"
      ? `/stilltouchedx/${u.id}`
      : `/marketplace/${u.id}`;
  }
  if (u.kind === "post") return "/social";
  if (u.kind === "story") return "/stories";
  return null;
}

export default function MediaAdminSection() {
  const { user } = useAuth();
  const isVaultVisible = ["owner", "admin"].includes(user?.role);
  const AREAS = isVaultVisible ? [...BASE_AREAS, WORN_VAULT_AREA] : BASE_AREAS;
  const [areaTab, setAreaTab] = useState("MARKETPLACE");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploaderQuery, setUploaderQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [confirmForce, setConfirmForce] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [reaperOpen, setReaperOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(uploaderQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [uploaderQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        area_scope: areaTab,
        sort,
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (debouncedQuery) params.set("uploader_query", debouncedQuery);
      const { data } = await api.get(`/admin/media?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [areaTab, debouncedQuery, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [areaTab, debouncedQuery, sort]);

  const openDetail = async (fileId) => {
    setDetailLoading(true);
    setDeleteError("");
    setConfirmForce(false);
    setSelected({ loading: true });
    try {
      const { data } = await api.get(`/admin/media/${fileId}`);
      setSelected(data);
    } catch (e) {
      setSelected(null);
      setError(formatError(e));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelected(null); setDeleteError(""); setConfirmForce(false); };

  const deleteFile = async (force = false) => {
    if (!selected || !selected.file) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/admin/media/${selected.file.file_id}${force ? "?force=true" : ""}`);
      closeDetail();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "MEDIA_IN_USE") {
        setDeleteError(detail.message || "Datei wird noch verwendet.");
      } else {
        setDeleteError(formatError(e));
      }
    } finally {
      setDeleting(false);
    }
  };

  const impersonateUploader = async () => {
    const uploaderId = selected?.file?.uploader_id;
    if (!uploaderId) return;
    try {
      const { data } = await api.post(`/admin/users/${uploaderId}/impersonate`);
      window.open(`/impersonate?token=${data.impersonation_token}`, "_blank");
    } catch (e) {
      setDeleteError(formatError(e));
    }
  };

  return (
    <div data-testid="media-admin-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#0A0A0A]">Medienverwaltung</h2>
        <div className="flex items-center gap-3">
          {areaTab !== "WORN_VAULT" && (
            <p className="text-xs text-[#999]">{total} Datei(en) im Bereich {areaTab}</p>
          )}
          <button
            data-testid="media-cleanup-open"
            onClick={() => setCleanupOpen(true)}
            className="h-8 px-3 text-xs font-semibold bg-[#0A0A0A] text-white hover:bg-[#222] flex items-center gap-1.5"
          >
            <Trash2 size={12} /> Verwaiste Medien bereinigen
          </button>
          <button
            data-testid="media-reaper-open"
            onClick={() => setReaperOpen(true)}
            className="h-8 px-3 text-xs font-semibold bg-red-700 text-white hover:bg-red-800 flex items-center gap-1.5"
          >
            <Skull size={12} /> Endgültige Löschung (Reaper)
          </button>
        </div>
      </div>
      {cleanupOpen && (
        <MediaCleanupDialog onClose={() => { setCleanupOpen(false); load(); }} />
      )}
      {reaperOpen && (
        <MediaReaperPanel onClose={() => { setReaperOpen(false); load(); }} />
      )}

      <div className="flex gap-1 border-b border-[#E5E5E5]">
        {AREAS.map((a) => {
          const Icon = a.icon;
          return (
            <button key={a.key} data-testid={`media-tab-${a.key}`}
              onClick={() => setAreaTab(a.key)}
              className={`h-10 px-4 text-sm border-b-2 transition-colors inline-flex items-center gap-1.5 ${areaTab === a.key ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-[#666] hover:text-[#0A0A0A]"}`}>
              {Icon && <Icon size={13} />} {a.label}
            </button>
          );
        })}
      </div>

      {areaTab === "WORN_VAULT" && isVaultVisible ? (
        <WornVaultSection />
      ) : (
        <>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-xs text-[#666] uppercase tracking-wider">Uploader-Suche</label>
        <input data-testid="media-uploader-search" value={uploaderQuery}
          onChange={(e) => setUploaderQuery(e.target.value)}
          placeholder="Username oder Anzeigename …"
          className="h-9 px-3 border border-[#E5E5E5] text-sm bg-white min-w-[240px] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
        {uploaderQuery && (
          <button type="button" onClick={() => setUploaderQuery("")} data-testid="media-uploader-clear"
            className="h-9 px-2 border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]">×</button>
        )}
        <label className="text-xs text-[#666] uppercase tracking-wider ml-3">Sortierung</label>
        <select data-testid="media-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-9 px-3 border border-[#E5E5E5] text-sm bg-white">
          <option value="date_desc">Neueste zuerst</option>
          <option value="date_asc">Älteste zuerst</option>
        </select>
        <button data-testid="media-refresh" onClick={() => load()}
          className="h-9 px-3 bg-[#0A0A0A] text-white text-xs hover:bg-[#222]">Neu laden</button>
      </div>

      {error && <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#999]" data-testid="media-empty">Keine Medien im Bereich {areaTab}.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="media-grid">
          {items.map((it) => (
            <button key={it.file_id} type="button" data-testid={`media-item-${it.file_id}`}
              onClick={() => openDetail(it.file_id)}
              className="text-left bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#0A0A0A] transition-colors">
              <div className="aspect-square bg-[#F5F5F5]">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${it.url}`} alt={it.original_filename || it.file_id}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.opacity = "0.2"; }} />
              </div>
              <div className="p-2 space-y-1">
                <p className="text-[11px] font-medium text-[#0A0A0A] truncate" title={it.original_filename}>{it.original_filename || "(ohne Name)"}</p>
                <p className="text-[10px] text-[#999] truncate" title={it.uploader_display_name}>{it.uploader_display_name}</p>
                <p className="text-[10px] text-[#CCC]">{(it.created_at || "").slice(0, 10)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-2">
          <button data-testid="media-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="h-9 px-3 border border-[#E5E5E5] text-sm disabled:opacity-30">Zurück</button>
          <span className="text-xs text-[#666]">Seite {page + 1} / {Math.ceil(total / PAGE_SIZE)}</span>
          <button data-testid="media-next" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}
            className="h-9 px-3 border border-[#E5E5E5] text-sm disabled:opacity-30">Weiter</button>
        </div>
      )}

      {selected && (
        <div data-testid="media-detail-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closeDetail}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Medien-Details</h3>
              <button data-testid="media-detail-close" onClick={closeDetail}
                className="w-8 h-8 flex items-center justify-center hover:bg-[#F5F5F5]">
                <X className="w-4 h-4" />
              </button>
            </div>
            {selected.loading || detailLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
              </div>
            ) : selected.file ? (
              <div className="grid md:grid-cols-2 gap-0">
                <div className="bg-[#F5F5F5] aspect-square md:aspect-auto md:min-h-[400px]">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${selected.file.url}`}
                    alt={selected.file.original_filename}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.opacity = "0.2"; }} />
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-[10px] text-[#999] uppercase tracking-wider">Dateiname</p>
                    <p className="text-sm text-[#0A0A0A] break-all" data-testid="media-detail-filename">{selected.file.original_filename || "(ohne Name)"}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-[#999] uppercase tracking-wider">Bereich</p>
                      <p className="text-sm text-[#0A0A0A]" data-testid="media-detail-area">{selected.file.area_scope || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#999] uppercase tracking-wider">Größe</p>
                      <p className="text-sm text-[#0A0A0A]">{selected.file.size ? `${(selected.file.size / 1024).toFixed(1)} KB` : "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#999] uppercase tracking-wider">Hochgeladen</p>
                      <p className="text-sm text-[#0A0A0A]">{(selected.file.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#999] uppercase tracking-wider">Format</p>
                      <p className="text-sm text-[#0A0A0A]">{selected.file.content_type || "—"}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#999] uppercase tracking-wider">Uploader</p>
                    <p className="text-sm text-[#0A0A0A]" data-testid="media-detail-uploader">{selected.file.uploader_display_name}</p>
                    {selected.file.uploader_id && (
                      <button type="button" onClick={impersonateUploader}
                        data-testid="media-detail-uploader-link"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        <LogIn size={11} /> Als {selected.file.uploader_handle ? `@${selected.file.uploader_handle}` : "Uploader"} einloggen →
                      </button>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-[#999] uppercase tracking-wider mb-1">Verwendung</p>
                    {selected.is_orphaned ? (
                      <p data-testid="media-detail-orphan-badge" className="inline-block px-2 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                        Verwaist — wird nirgends verwendet
                      </p>
                    ) : (
                      <div data-testid="media-detail-usage-list" className="space-y-1 max-h-32 overflow-y-auto border border-[#E5E5E5] p-2 bg-[#FAFAFA]">
                        {selected.usage.map((u, idx) => {
                          const href = usageHref(u);
                          const label = (
                            <span className="truncate">
                              <span className="font-mono text-[10px] uppercase mr-2 text-[#999]">{u.kind}</span>
                              {u.public_item_no || u.title || u.id}
                            </span>
                          );
                          return (
                            <div key={`${u.kind}-${u.id}-${idx}`} className="text-xs flex items-center justify-between gap-2">
                              {href ? (
                                <a href={href} target="_blank" rel="noreferrer"
                                  data-testid={`media-detail-usage-link-${idx}`}
                                  className="flex-1 min-w-0 flex items-center text-[#0A0A0A] hover:text-blue-600 hover:underline">
                                  {label}
                                </a>
                              ) : (
                                <span className="flex-1 min-w-0 text-[#666]">{label}</span>
                              )}
                              <span className="text-[10px] text-[#999] shrink-0">{u.area_scope || ""}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {deleteError && (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-xs" data-testid="media-detail-delete-error">
                      {deleteError}
                    </div>
                  )}
                  <div className="pt-2 border-t border-[#E5E5E5] space-y-3">
                    {selected.is_orphaned ? (
                      <div className="flex gap-2">
                        <button data-testid="media-detail-delete-orphan" disabled={deleting} onClick={() => deleteFile(false)}
                          className="flex-1 h-10 bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50">
                          {deleting ? "Lösche …" : "Verwaiste Datei löschen"}
                        </button>
                        <button data-testid="media-detail-cancel" onClick={closeDetail}
                          className="h-10 px-4 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">Abbrechen</button>
                      </div>
                    ) : confirmForce ? (
                      <div data-testid="media-detail-force-confirm" className="space-y-3">
                        <div className="flex items-start gap-2 px-3 py-3 bg-red-50 border border-red-300 text-red-800">
                          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                          <div className="text-xs leading-relaxed">
                            <p className="font-bold mb-1">Achtung: Diese Datei wird aktuell verwendet!</p>
                            <p>
                              Die Datei ist in <b>{selected.usage.length}</b> Live-Inhalt(en) eingebunden.
                              Wenn Du sie löschst, werden diese Inhalte unbrauchbar (fehlendes Bild) —
                              das lässt sich nur schwer rückgängig machen. Betroffen:
                            </p>
                            <ul className="mt-1 list-disc list-inside space-y-0.5">
                              {selected.usage.slice(0, 8).map((u, idx) => (
                                <li key={`warn-${u.kind}-${u.id}-${idx}`} className="truncate">
                                  <span className="font-mono uppercase mr-1">{u.kind}</span>
                                  {u.public_item_no || u.title || u.id}
                                </li>
                              ))}
                              {selected.usage.length > 8 && (
                                <li>… und {selected.usage.length - 8} weitere</li>
                              )}
                            </ul>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button data-testid="media-detail-force-confirm-yes" disabled={deleting} onClick={() => deleteFile(true)}
                            className="flex-1 h-10 bg-red-700 text-white text-xs font-bold hover:bg-red-800 disabled:opacity-50">
                            {deleting ? "Lösche …" : "Ja, endgültig löschen"}
                          </button>
                          <button data-testid="media-detail-force-confirm-cancel" disabled={deleting} onClick={() => setConfirmForce(false)}
                            className="h-10 px-4 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">Abbrechen</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button data-testid="media-detail-delete-force" disabled={deleting} onClick={() => setConfirmForce(true)}
                          className="flex-1 h-10 bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50">
                          Löschen erzwingen …
                        </button>
                        <button data-testid="media-detail-cancel" onClick={closeDetail}
                          className="h-10 px-4 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">Abbrechen</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
